import { RootState } from "redux/store";
import { useEffect, useRef, useState } from "react";
import { getStockTable } from "redux/slices/Stocks";
import { SubDepartments } from "./SubDepartments";
import { Spinner, Title, FormInput, Modal } from "components";
import { getStoreGroups } from "redux/slices/Accounts";
import { DepartmentStocks } from "./DepartmentStocks";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { Link, useNavigate, useParams } from "react-router-dom";
import { cleanDepartmentDetails, createDepartment, getDepartmentDetails, getDepartmentStocks, getDepartments, updateDepartment, updateDepartmentField, cleanSubDepartmentDetails, getSubDepartments } from "redux/slices/PageMemory/StocksDepartmentPageSlice";
import { ManagedDepartment } from "models/dbModels/managedDepartments/ManagedDepartment";

export function NewDepartment(props: any) {
	const form = useRef(null);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { departmentId, subDepartmentsId } = useParams<string>();
	const departmentIdInt = parseInt(departmentId || "0");
	const [loading, setLoading] = useState<boolean>(false);
	const [isValidated, setValidated] = useState<boolean>(false);
	const [isNewDepartment, setIsNewDepartment] = useState<boolean>(true);
	const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
	const stockDepartmentPageSlice = useAppSelector((state: RootState) => state.stockDepartmentPageSlice);

	function reloadDepartmentStock(keyword: string | undefined = undefined, subdepartments: number[] | undefined = undefined, offset: number | undefined = undefined, limit: number | undefined = undefined) {
		let selectedSubDepartments: any[] = [];
		dispatch(getDepartmentStocks({
			keyword: keyword || stockDepartmentPageSlice.stocksKeyword,
			departmentIds: [departmentIdInt],
			subdepartmentIds: subdepartments || stockDepartmentPageSlice.selectedSubDepartmentIds,
			offset: offset || (stockDepartmentPageSlice.pageIndex * stockDepartmentPageSlice.pageSize),
			limit: limit || stockDepartmentPageSlice.pageSize,
		}));
	}

	useEffect(() => {
		setValidated(false);
		dispatch(cleanDepartmentDetails());
		dispatch(cleanSubDepartmentDetails());
		dispatch(getDepartments());
		dispatch(getStoreGroups());
		dispatch(getStockTable({}));
		if (departmentIdInt) {
			dispatch(getDepartmentDetails([departmentIdInt])).then(() => {
				dispatch(getSubDepartments(departmentIdInt)).then((data) => {
					updateDepartmentField({ key: "subDepartments", value: data.data });
					reloadDepartmentStock();
				});
			});
			setIsNewDepartment(false);
		} else {
			setIsNewDepartment(true);
		}
	}, [departmentIdInt]);

	useEffect(() => {
		if (stockDepartmentPageSlice.loadStocks && stockDepartmentPageSlice.subDepartments.length > 0) {
			let subDepartmentsIds: any[] = [];
			stockDepartmentPageSlice.subDepartments.forEach((item: any) => {
				if (item.departmentId === stockDepartmentPageSlice.id) subDepartmentsIds.push(item.id);
			});
			if (subDepartmentsIds.length > 0) {
				reloadDepartmentStock();
			}
		}
	}, [stockDepartmentPageSlice.loadStocks]);

	useEffect(() => {
		if (subDepartmentsId) {
			dispatch(updateDepartmentField({ key: "showSubdepartmentPopup", value: true }));
		}
	}, [subDepartmentsId]);

	useEffect(() => {
		if (stockDepartmentPageSlice.selectedSubDepartmentIds && stockDepartmentPageSlice.subDepartments.length > 0) {
			reloadDepartmentStock();
		}
	}, [stockDepartmentPageSlice.selectedSubDepartmentIds]);

	useEffect(() => {
		if (stockDepartmentPageSlice.selectSubDepartmentPopup === true) {
			dispatch(updateDepartmentField({ key: "selectedSubDepartment", value: 0 }));
		}
	}, [stockDepartmentPageSlice.selectSubDepartmentPopup, stockDepartmentPageSlice.stockPopup]);

	function handleSubmit() {
		setShowConfirmModal(false);
		setLoading(true);
		// @ts-ignore
		if (form.current.checkValidity() === true) {
			const deptModel: ManagedDepartment = {id: stockDepartmentPageSlice.id, name: stockDepartmentPageSlice.name, number: stockDepartmentPageSlice.number, lock: stockDepartmentPageSlice.lock, modelId: stockDepartmentPageSlice.modelId};
			if (isNewDepartment) {
				dispatch(createDepartment(deptModel)).then(() => handleUpdate()).catch(() => setLoading(false));
			} else {
				dispatch(updateDepartment(deptModel)).then(() => handleUpdate()).catch(() => setLoading(false));
			}
		} else {
			setLoading(false);
		}
		setValidated(true);
	}

	function handleUpdate() {
		setLoading(false);
		navigate("/app/departments");
	}

	function handleChange(option: any, optionValue: any) {
		dispatch(updateDepartmentField({ key: option, value: optionValue }));
	}

	return (
		<>
			<Title pageTitle={isNewDepartment ? "New Department" : "Update Department"} crumbs={[{ name: "Departments", path: "/app/departments" }]} />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<Col>
									<Form onSubmit={handleSubmit} validated={isValidated} ref={form}>
										<Row>
											<h4 className="mt-2">Department Details</h4>
											<Col md={6}>
												<FormInput
													required
													label="Name"
													type="text"
													name="department-name"
													placeholder="Enter department name"
													containerClass={"mb-1"}
													value={stockDepartmentPageSlice.name}
													key="text"
													onChange={(e) => handleChange("name", e.target.value)}
												/>
											</Col>
											<Col md={3}>
												<FormInput
													label="No."
													type="text"
													name="department-Number"
													placeholder="Enter department number"
													containerClass={"mb-1"}
													value={stockDepartmentPageSlice.number}
													key="text"
													onChange={(e) => handleChange("number", e.target.value)}
												/>
											</Col>
											<Col md={3}>
												<Form.Group>
													<Form.Label className="form-check-label" htmlFor="exampleCheckbox">Lock</Form.Label>
													<div>
														<Form.Check
															type="switch"
															id="lock"
															// label={department.lock ? "Active" : "Inactive"}
															checked={stockDepartmentPageSlice.lock ? true : false}
															onChange={(e) => handleChange("lock", !stockDepartmentPageSlice.lock)}
														/>
													</div>
												</Form.Group>
											</Col>
										</Row>
										{!isNewDepartment &&
											<Row>
												<Col md={6}>
													<SubDepartments departmentId={departmentIdInt} />
												</Col>
												<Col md={6}>
													<DepartmentStocks departmentId={departmentIdInt} />
												</Col>
											</Row>
										}
										<Row>
											<Modal
												title="Confirm"
												show={showConfirmModal}
												buttonType="primary"
												buttonText="Continue"
												buttonCloseText="Cancel"
												handleSubmit={handleSubmit}
												handleClose={() => setShowConfirmModal(false)}
												description={
													<>
														<p>You are about to sync the department & sub department down to all stores.</p>
														<p>To sync the stock lines within, go to Managed Stocks &gt; Sync &gt; Select Department/Sub Department.</p>
														<p>Are you sure you want to continue?</p>
													</>
												}
											/>
										</Row>
										<Row className="mt-3">
											<Col sm={6}>
												<Link to="/app/departments" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold">
													<i className="mdi mdi-arrow-left"></i> Back to All Departments {" "}
												</Link>
											</Col>
											<Col sm={6} className="d-flex flex-row-reverse">
												<Button variant="primary" className="btn cta-button" disabled={loading} onClick={() => setShowConfirmModal(true)}>
													{loading ? <><Spinner className="spinner-border-sm me-1" tag="span" color="white" /></> : <></>}
													{loading ? <>Saving...</> : <>Save</>}
												</Button>
											</Col>
										</Row>
									</Form>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</>
	);
}